<template>
  <div class="membership-orders-page">
    <section class="page-toolbar">
      <div>
        <strong>会员订单</strong>
        <span>查看 VIP 卡购买、激活、发货、退款和赠送状态。</span>
      </div>
      <div class="toolbar-actions">
        <label class="test-toggle">
          <input v-model="includeTest" type="checkbox" @change="handleTestChange">
          <span>包含测试卡</span>
        </label>
        <button class="ghost-button" type="button" :disabled="loading" @click="loadOrders">
          {{ loading ? "加载中..." : "刷新" }}
        </button>
      </div>
    </section>

    <section v-if="error" class="state-card state-card--error">
      {{ error }}
      <button class="link-button" type="button" @click="loadOrders">重新加载</button>
    </section>

    <template v-else>
      <section class="summary-grid">
        <button
          v-for="item in summaryCards"
          :key="item.status"
          type="button"
          class="summary-card"
          :class="{ active: status === item.status, 'summary-card--received': item.status === 'RECEIVED' }"
          @click="selectSummary(item)"
        >
          <span class="summary-label">{{ item.label }}</span>
          <template v-if="item.status === 'RECEIVED'">
            <strong>¥{{ formatMoney(item.totalAmount) }}</strong>
            <small>{{ item.count }} 张卡已到账</small>
            <small>卡款 ¥{{ formatMoney(item.cardAmount) }} · 运费 ¥{{ formatMoney(item.shippingAmount) }}</small>
          </template>
          <template v-else>
            <strong>{{ item.count }}</strong>
            <small v-if="item.status === 'ALL'">共 {{ item.count }} 笔会员卡订单</small>
            <small v-else>卡款 ¥{{ formatMoney(item.cardAmount) }}</small>
          </template>
        </button>
      </section>

      <section class="panel filter-panel">
        <div class="filter-tabs">
          <button
            v-for="item in filters"
            :key="item.value"
            type="button"
            :class="{ active: status === item.value }"
            @click="selectStatus(item.value)"
          >{{ item.label }}</button>
        </div>
        <div class="search-field">
          <span>⌕</span>
          <input
            v-model.trim="keyword"
            type="search"
            placeholder="搜索卡号、用户昵称、OpenID、蛇宠或快递单号"
            @keyup.enter="searchOrders"
          >
          <button class="search-button" type="button" @click="searchOrders">搜索</button>
        </div>
      </section>

      <section class="panel order-panel">
        <div v-if="loading" class="empty-state">正在加载会员订单...</div>
        <div v-else-if="!orders.length" class="empty-state">当前条件下没有会员订单</div>
        <div v-else class="table-wrap">
          <table class="order-table">
            <thead>
              <tr>
                <th>用户</th>
                <th>VIP卡号</th>
                <th>套餐</th>
                <th>金额</th>
                <th>状态</th>
                <th>绑定蛇宠</th>
                <th>预约/物流</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="order.id">
                <td>
                  <strong>{{ order.nickname || "未命名用户" }}</strong>
                  <small>ID {{ order.userId }}</small>
                </td>
                <td>
                  <span class="mono">{{ order.cardNo }}</span>
                  <div class="tag-row">
                    <span v-if="order.isTest" class="tag tag-test">测试</span>
                    <span v-if="order.gifted" class="tag tag-gift">受赠</span>
                  </div>
                </td>
                <td>
                  <strong>{{ order.durationMonths || 3 }}个月 · {{ order.mouseQuantity || 15 }}只</strong>
                  <small>{{ order.weightLabel || "-" }}</small>
                </td>
                <td>
                  <strong>¥{{ formatMoney(order.cardAmount) }}</strong>
                  <small>运费 ¥{{ formatMoney(order.shippingAmount) }}</small>
                </td>
                <td>
                  <span class="status-pill" :class="statusClass(order.status)">{{ order.statusText }}</span>
                  <small v-if="order.refundStatus">退款 {{ refundStatusText(order.refundStatus) }}</small>
                </td>
                <td>
                  <strong>{{ order.petName || "-" }}</strong>
                  <small>{{ order.petSpecies || "未绑定" }}</small>
                </td>
                <td>
                  <strong>{{ order.scheduledShipDate ? formatDate(order.scheduledShipDate) : "-" }}</strong>
                  <small v-if="order.trackingNo">{{ order.carrier || "快递" }} · {{ order.trackingNo }}</small>
                  <small v-else>{{ shipmentStatusText(order.shipmentStatus) }}</small>
                </td>
                <td>{{ formatTime(order.createdAt) }}</td>
                <td><button class="detail-button" type="button" @click="selectedOrder = order">详情</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer v-if="!loading && totalCount" class="pagination-bar">
          <span>
            共 {{ totalCount }} 笔，第 {{ page }} / {{ totalPages }} 页
          </span>
          <div class="pagination-actions">
            <label>
              每页
              <select v-model.number="pageSize" @change="changePageSize">
                <option :value="20">20</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
              条
            </label>
            <button type="button" :disabled="page <= 1" @click="goToPage(page - 1)">上一页</button>
            <button
              v-for="pageNumber in pageNumbers"
              :key="pageNumber"
              type="button"
              :class="{ active: pageNumber === page }"
              @click="goToPage(pageNumber)"
            >{{ pageNumber }}</button>
            <button type="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">下一页</button>
          </div>
        </footer>
      </section>
    </template>

    <div v-if="selectedOrder" class="modal-backdrop" @click.self="selectedOrder = null">
      <section class="modal-card">
        <header class="modal-head">
          <div>
            <span>VIP CARD ORDER</span>
            <h2>{{ selectedOrder.cardNo }}</h2>
            <p>{{ selectedOrder.nickname || "未命名用户" }} · {{ selectedOrder.statusText }}</p>
          </div>
          <button type="button" @click="selectedOrder = null">×</button>
        </header>
        <div class="detail-grid">
          <div><span>用户 ID</span><strong>{{ selectedOrder.userId }}</strong></div>
          <div><span>VIP卡款</span><strong>¥{{ formatMoney(selectedOrder.cardAmount) }}</strong></div>
          <div><span>供应商成本</span><strong>¥{{ formatMoney(selectedOrder.supplierCostAmount) }}</strong></div>
          <div><span>激活运费</span><strong>¥{{ formatMoney(selectedOrder.shippingAmount) }}</strong></div>
          <div><span>绑定蛇宠</span><strong>{{ selectedOrder.petName || "未绑定" }}</strong></div>
          <div><span>会员有效期</span><strong>{{ membershipPeriod(selectedOrder) }}</strong></div>
          <div><span>激活订单号</span><strong class="mono">{{ selectedOrder.activationOrderNo || "-" }}</strong></div>
          <div><span>预约发货日期</span><strong>{{ selectedOrder.scheduledShipDate ? formatDate(selectedOrder.scheduledShipDate) : "-" }}</strong></div>
          <div><span>物流状态</span><strong>{{ shipmentStatusText(selectedOrder.shipmentStatus) }}</strong></div>
          <div><span>快递单号</span><strong>{{ selectedOrder.carrier || "" }} {{ selectedOrder.trackingNo || "-" }}</strong></div>
          <div><span>赠卡人</span><strong>{{ selectedOrder.gifterName || "-" }}</strong></div>
          <div><span>原付款人</span><strong>{{ selectedOrder.originalPayerName || "-" }}</strong></div>
          <div class="wide"><span>收货地址</span><strong>{{ receiverText(selectedOrder) }}</strong></div>
          <div class="wide"><span>退款状态</span><strong>{{ refundStatusText(selectedOrder.refundStatus) }}</strong></div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { fetchMembershipOrders } from "../api/adminSystem"

export default {
  name: "MembershipOrdersView",
  data() {
    return {
      loading: false,
      error: "",
      status: "ALL",
      keyword: "",
      includeTest: false,
      summary: [],
      total: {},
      received: {},
      orders: [],
      page: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0,
      selectedOrder: null,
      filters: [
        { value: "ALL", label: "全部" },
        { value: "CARD_READY", label: "待激活" },
        { value: "ACTIVE", label: "已激活" },
        { value: "REFUNDED", label: "已退款" },
        { value: "CANCELLED", label: "已取消" }
      ]
    }
  },
  computed: {
    summaryCards() {
      const list = this.summary.map(item => ({
        status: item.status,
        label: item.statusText,
        count: Number(item.orderCount || 0),
        cardAmount: Number(item.cardAmount || 0)
      }))
      return [
        {
          status: "RECEIVED",
          label: "已收款",
          count: Number(this.received.orderCount || 0),
          cardAmount: Number(this.received.cardAmount || 0),
          shippingAmount: Number(this.received.shippingAmount || 0),
          totalAmount: Number(this.received.totalAmount || 0)
        },
        {
          status: "ALL",
          label: "全部订单",
          count: Number(this.total.orderCount || 0),
          cardAmount: Number(this.total.cardAmount || 0)
        },
        ...list
      ]
    },
    pageNumbers() {
      if (this.totalPages <= 0) return []
      const start = Math.max(1, Math.min(this.page - 2, this.totalPages - 4))
      const end = Math.min(this.totalPages, start + 4)
      return Array.from({ length: end - start + 1 }, (_, index) => start + index)
    }
  },
  mounted() {
    this.loadOrders()
  },
  methods: {
    async loadOrders() {
      if (this.loading) return
      this.loading = true
      this.error = ""
      try {
        const response = await fetchMembershipOrders({
          status: this.status === "ALL" ? undefined : this.status,
          keyword: this.keyword || undefined,
          includeTest: this.includeTest,
          page: this.page,
          pageSize: this.pageSize
        })
        const data = response.data || {}
        this.summary = Array.isArray(data.summary) ? data.summary : []
        this.total = data.total || {}
        this.received = data.received || {}
        this.orders = Array.isArray(data.orders) ? data.orders : []
        this.page = Number(data.page || 1)
        this.pageSize = Number(data.pageSize || this.pageSize)
        this.totalCount = Number(data.totalCount || 0)
        this.totalPages = Number(data.totalPages || 0)
      } catch (error) {
        this.error = error.message || "会员订单加载失败"
      } finally {
        this.loading = false
      }
    },
    selectSummary(item) {
      if (item.status === "RECEIVED") return
      this.selectStatus(item.status)
    },
    selectStatus(value) {
      this.status = value
      this.page = 1
      this.loadOrders()
    },
    searchOrders() {
      this.page = 1
      this.loadOrders()
    },
    handleTestChange() {
      this.page = 1
      this.loadOrders()
    },
    changePageSize() {
      this.page = 1
      this.loadOrders()
    },
    goToPage(value) {
      if (value < 1 || value > this.totalPages || value === this.page) return
      this.page = value
      this.loadOrders()
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatTime(value) {
      return value ? String(value).replace("T", " ").slice(0, 16) : "-"
    },
    formatDate(value) {
      return value ? String(value).slice(0, 10) : "-"
    },
    statusClass(status) {
      return {
        CARD_READY: "status-ready",
        ACTIVE: "status-active",
        REFUNDING: "status-warning",
        REFUNDED: "status-refunded",
        CANCELLED: "status-cancelled"
      }[status] || "status-pending"
    },
    shipmentStatusText(status) {
      return {
        PENDING_PAYMENT: "待支付运费",
        PENDING_SHIPMENT: "等待商家发货",
        SHIPPED: "运输中",
        COMPLETED: "已签收",
        REFUNDED: "已退款",
        CANCELLED: "已取消"
      }[status] || "未进入发货"
    },
    refundStatusText(status) {
      if (!status) return "无退款"
      return { PROCESSING: "退款处理中", SUCCESS: "退款成功", CLOSED: "退款关闭", ABNORMAL: "退款异常", FAILED: "退款失败" }[status] || status
    },
    membershipPeriod(order) {
      if (!order.membershipStartAt || !order.membershipEndAt) return "-"
      return `${this.formatDate(order.membershipStartAt)} 至 ${this.formatDate(order.membershipEndAt)}`
    },
    receiverText(order) {
      if (!order.receiverAddress) return "-"
      return `${order.receiverName || ""} ${order.receiverPhone || ""} ${order.receiverAddress}`.trim()
    }
  }
}
</script>

<style scoped>
.membership-orders-page { max-width: 1500px; color: #25322d; }
.page-toolbar, .toolbar-actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-toolbar { margin-bottom: 16px; align-items: flex-end; }
.page-toolbar strong { display: block; font-size: 20px; }
.page-toolbar span { display: block; margin-top: 5px; color: #7a8882; font-size: 12px; }
.toolbar-actions { align-items: center; }
.test-toggle { display: flex; height: 36px; padding: 0 12px; align-items: center; gap: 7px; border: 1px solid #dfe6e2; border-radius: 10px; background: #fff; color: #52635b; font-size: 12px; }
.ghost-button, .search-button, .detail-button, .link-button { border: 0; border-radius: 9px; cursor: pointer; font: inherit; }
.ghost-button { height: 36px; padding: 0 14px; border: 1px solid #dce4df; background: #fff; color: #44564e; }
.summary-grid { display: grid; margin-bottom: 14px; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 10px; }
.summary-card { display: flex; min-height: 92px; padding: 15px; border: 1px solid #e2e8e4; border-radius: 14px; flex-direction: column; align-items: flex-start; background: #fff; color: #34443d; text-align: left; cursor: pointer; }
.summary-card.active { border-color: #4a8c6c; box-shadow: 0 0 0 2px rgba(74,140,108,.1); }
.summary-card--received { border-color: #b9ddc9; background: linear-gradient(145deg, #f5fbf7, #eef8f2); cursor: default; }
.summary-label { color: #7e8b85; font-size: 11px; }
.summary-card strong { margin-top: 7px; font-size: 24px; line-height: 1; }
.summary-card small { margin-top: 7px; color: #98a39e; font-size: 10px; }
.summary-card--received strong { color: #286e50; }
.summary-card--received small { color: #698577; }
.panel { margin-bottom: 14px; overflow: hidden; border: 1px solid #e3e9e5; border-radius: 15px; background: #fff; }
.filter-panel { display: flex; padding: 13px 15px; align-items: center; gap: 14px; }
.filter-tabs { display: flex; gap: 2px; flex-wrap: wrap; }
.filter-tabs button { height: 32px; padding: 0 12px; border: 0; border-radius: 8px; background: transparent; color: #6e7b75; font-size: 12px; cursor: pointer; }
.filter-tabs button.active { background: #eaf3ee; color: #27684d; font-weight: 700; }
.search-field { display: flex; min-width: 280px; height: 36px; margin-left: auto; padding-left: 11px; border: 1px solid #dfe6e2; border-radius: 10px; align-items: center; background: #fafcfb; }
.search-field input { flex: 1; min-width: 0; height: 34px; border: 0; outline: none; background: transparent; color: #34443d; font: inherit; font-size: 12px; }
.search-button { height: 28px; margin-right: 4px; padding: 0 11px; background: #28382f; color: #fff; font-size: 11px; }
.table-wrap { overflow-x: auto; }
.order-table { width: 100%; min-width: 1180px; border-collapse: collapse; }
.order-table th, .order-table td { padding: 13px 12px; border-bottom: 1px solid #edf0ee; text-align: left; vertical-align: middle; }
.order-table th { background: #f8faf9; color: #84918b; font-size: 10px; letter-spacing: .4px; }
.order-table td { color: #4f5f57; font-size: 12px; }
.order-table td strong, .order-table td small { display: block; }
.order-table td strong { color: #2f3f37; font-size: 12px; }
.order-table td small { margin-top: 4px; color: #96a19b; font-size: 10px; }
.mono { font-family: "SFMono-Regular", Consolas, monospace; font-size: 11px; }
.tag-row { display: flex; margin-top: 5px; gap: 4px; }
.tag { padding: 2px 6px; border-radius: 999px; font-size: 9px; }
.tag-test { background: #fff1d6; color: #9a6b1e; }
.tag-gift { background: #e9f3ec; color: #3d7858; }
.status-pill { display: inline-flex; padding: 4px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
.status-ready { background: #fff1d8; color: #9a6b21; }
.status-active { background: #e7f3eb; color: #2f7452; }
.status-refunded { background: #f1eef7; color: #725b92; }
.status-warning { background: #fff0e1; color: #a86029; }
.status-cancelled { background: #f1f3f2; color: #7f8a85; }
.status-pending { background: #edf2fb; color: #476b9c; }
.detail-button { padding: 6px 10px; background: #eef4f1; color: #3d7258; font-size: 11px; }
.pagination-bar { display: flex; padding: 13px 15px; border-top: 1px solid #edf0ee; align-items: center; justify-content: space-between; gap: 14px; color: #7f8d86; font-size: 11px; }
.pagination-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }
.pagination-actions label { display: flex; margin-right: 7px; align-items: center; gap: 5px; }
.pagination-actions select { height: 29px; padding: 0 6px; border: 1px solid #dfe6e2; border-radius: 7px; outline: none; background: #fff; color: #44564e; font: inherit; }
.pagination-actions button { min-width: 30px; height: 29px; padding: 0 9px; border: 1px solid #dfe6e2; border-radius: 7px; background: #fff; color: #52635b; font: inherit; cursor: pointer; }
.pagination-actions button.active { border-color: #4a8c6c; background: #eaf3ee; color: #27684d; font-weight: 700; }
.pagination-actions button:disabled { opacity: .4; cursor: not-allowed; }
.empty-state { padding: 55px 20px; color: #8c9892; text-align: center; font-size: 13px; }
.state-card { padding: 22px; border-radius: 14px; background: #fff; color: #65736c; }
.state-card--error { color: #a7443d; }
.link-button { margin-left: 10px; padding: 5px 9px; background: #f2f5f3; color: #4e6f5e; }
.modal-backdrop { position: fixed; inset: 0; z-index: 80; display: grid; padding: 24px; place-items: center; background: rgba(20,29,25,.46); }
.modal-card { width: min(760px, 100%); max-height: 88vh; overflow: auto; border-radius: 18px; background: #fff; box-shadow: 0 22px 60px rgba(20,40,30,.22); }
.modal-head { display: flex; padding: 20px 22px; align-items: flex-start; justify-content: space-between; background: #f5f8f6; }
.modal-head span { color: #8b9891; font-size: 9px; letter-spacing: 1.4px; }
.modal-head h2 { margin: 7px 0 0; font-family: "SFMono-Regular", Consolas, monospace; font-size: 17px; }
.modal-head p { margin: 6px 0 0; color: #74827b; font-size: 12px; }
.modal-head button { width: 32px; height: 32px; border: 0; border-radius: 9px; background: #fff; font-size: 20px; cursor: pointer; }
.detail-grid { display: grid; padding: 20px 22px 24px; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; }
.detail-grid div { padding: 12px; border-radius: 10px; background: #f8faf9; }
.detail-grid .wide { grid-column: 1 / -1; }
.detail-grid span, .detail-grid strong { display: block; }
.detail-grid span { color: #8b9791; font-size: 10px; }
.detail-grid strong { margin-top: 5px; color: #36463e; font-size: 12px; line-height: 1.55; word-break: break-all; }
@media (max-width: 780px) {
  .page-toolbar, .filter-panel { align-items: stretch; flex-direction: column; }
  .search-field { width: 100%; min-width: 0; margin-left: 0; box-sizing: border-box; }
  .pagination-bar { align-items: flex-start; flex-direction: column; }
  .detail-grid { grid-template-columns: 1fr; }
  .detail-grid .wide { grid-column: auto; }
}
</style>
